class VideoModalOpener extends HTMLElement {
  constructor() {
    super();

    // Bound handlers so we can add/remove listeners cleanly
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.modal = null;
    this.button = null;
    this.productId = null;
    this.videoContainer = null;
    this._observer = null;
    this.overviewVideoId = null;
  }

  connectedCallback() {
    // 1. Find the trigger button inside the custom element
    this.button = this.querySelector('button');
    if (!this.button) return;

    // 2. Find the modal element
    const modalId = this.dataset.modal;
    this.overviewVideoId = this.dataset.videoId || null;
    if (!modalId || !this.overviewVideoId) return;

    this.modal = document.getElementById(modalId);

    if (!this.modal) return;

    // 3. Derive product id (or allow explicit data-product-id)
    this.productId =
      this.dataset.productId || modalId.replace(/^video-modal-/, '');

    // 4. Find the video container inside the modal
    this.videoContainer =
      this.modal.querySelector(`#video-player-${this.productId}`) ||
      this.modal.querySelector('[data-video-player]') || // fallback hook
      null;

    // 5. Wire up the button
    this.button.addEventListener('click', this.handleButtonClick);

    // 6. Clicks inside modal (backdrop + close button)
    this.modal.addEventListener('click', this.handleModalClick);

    // 7. Escape key closes modal
    document.addEventListener('keydown', this.handleKeydown);

    // 8. Observe open/close attribute to lazy-load and clean up
    this._observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          if (this.isModalOpen()) {
            this.loadVideoContent();
          } else {
            this.closeVideo(); // safe to call even if already empty
          }
        }
      }
    });

    this._observer.observe(this.modal, {
      attributes: true,
      attributeFilter: ['open'],
    });

    // Optional: if the modal is already open on load (rare), populate it
    if (this.isModalOpen()) {
      this.loadVideoContent();
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleButtonClick);
    }
    if (this.modal) {
      this.modal.removeEventListener('click', this.handleModalClick);
    }
    document.removeEventListener('keydown', this.handleKeydown);

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  // ----------------------
  // Event handlers
  // ----------------------

  handleButtonClick(event) {
    event.preventDefault();
    this.openModal();
  }

  handleModalClick(event) {
    const target = event.target;

    // Explicit close button inside modal
    if (
      target.classList?.contains('js-close-modal') ||
      target.closest?.('.js-close-modal')
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.closeVideoAndModal();
      return;
    }

    // Clicked backdrop (dialog element itself)
    if (target === this.modal) {
      this.closeVideoAndModal();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isModalOpen()) {
      this.closeVideoAndModal();
    }
  }

  // ----------------------
  // Modal helpers
  // ----------------------

  openModal() {
    if (!this.modal) return;

    // Prefer native <dialog>
    if (typeof this.modal.showModal === 'function') {
      this.modal.showModal();
    }
    // Or a custom modal with .open()
    else if (typeof this.modal.open === 'function') {
      this.modal.open(this.button || null);
    }
    // Fallback: toggle an "open" attribute
    else {
      this.modal.setAttribute('open', '');
    }

    // In case the implementation doesn't use the "open" attribute,
    // make sure content is loaded now.
    this.loadVideoContent();
  }

  closeModal() {
    if (!this.modal) return;

    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
    }
  }

  isModalOpen() {
    if (!this.modal) return false;

    // Native dialog has .open boolean
    if (this.modal instanceof HTMLDialogElement) {
      return this.modal.open;
    }

    // Generic modal via "open" attribute
    return this.modal.hasAttribute('open');
  }

  closeVideoAndModal() {
    this.closeVideo();
    this.closeModal();
  }

  // ----------------------
  // Video logic
  // ----------------------

  loadVideoContent() {
    const container = this.getVideoContainer();
    if (!container || !this.productId) return;

    // Prevent double-load when button-click + observer both fire
    if (container.dataset.loaded === 'true') return;

    const videos = this.getVideoData(this.productId);
    if (!Array.isArray(videos) || videos.length === 0) return;

    // Clear previous content (if any)
    container.innerHTML = '';

    if (videos.length === 1) {
      this.createSingleVideo(container, videos[0]);
    } else {
      this.createVideoPlaylist(container, videos);
    }

    container.dataset.loaded = 'true';
  }

  closeVideo() {
    const container = this.getVideoContainer();
    if (!container) return;

    const iframes = container.querySelectorAll('iframe');

    // Politely ask YouTube iframes to pause
    iframes.forEach((iframe) => {
      try {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          '*',
        );
      } catch (e) {
        // Ignore cross-origin / timing errors
      }
    });

    // Remove all video DOM so playback truly stops
    container.innerHTML = '';
    container.dataset.loaded = 'false';
  }

  getVideoContainer() {
    if (this.videoContainer && this.modal?.contains(this.videoContainer)) {
      return this.videoContainer;
    }

    if (!this.modal || !this.productId) return null;

    this.videoContainer =
      this.modal.querySelector(`#video-player-${this.productId}`) ||
      this.modal.querySelector('[data-video-player]') ||
      null;

    return this.videoContainer;
  }

  getVideoData(productId) {
    // Expect: <script type="application/json" data-video-modal-data="PRODUCT_ID">...</script>
    const script = document.querySelector(
      `script[data-video-modal-data="${productId}"]`,
    );
    if (!script) return [];

    try {
      const raw = script.textContent || '{}';
      const data = JSON.parse(raw);
      return Array.isArray(data.videos) ? data.videos : [];
    } catch (e) {
      console.warn('Could not parse video modal data:', e);
      return [];
    }
  }

  // ----------------------
  // Rendering helpers
  // ----------------------

  createSingleVideo(container, videoData) {
    // Optional: maintain 16:9 ratio using a wrapper
    let overviewID = this.overviewVideoId;
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.width = '100%';
    wrapper.style.paddingTop = '56.25%'; // 16:9 aspect ratio

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${overviewID}?autoplay=1&rel=0&enablejsapi=1`;
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
    );
    iframe.allowFullscreen = true;
    iframe.title = videoData.title || '';

    wrapper.appendChild(iframe);
    container.appendChild(wrapper);
  }

  createVideoPlaylist(container, videoDataArray) {
    let currentIndex = 0;

    const playlistContainer = document.createElement('div');
    playlistContainer.className = 'video-playlist';
    playlistContainer.style.cssText =
      'width: 100%; height: 100%; display: flex; flex-direction: column; gap: 12px;';

    const videoFrame = document.createElement('div');
    videoFrame.className = 'video-frame';
    videoFrame.style.cssText =
      'position: relative; width: 100%; flex: 1 1 auto; padding-top: 56.25%;';

    const controls = document.createElement('div');
    controls.className = 'playlist-controls';
    controls.style.cssText =
      'display: flex; gap: 12px; align-items: center; justify-content: center; flex-wrap: wrap;';

    const prevBtn = document.createElement('button');
    prevBtn.textContent = '← Previous';
    prevBtn.type = 'button';
    prevBtn.style.cssText =
      'padding: 6px 12px; border-radius: 4px; border: none; cursor: pointer;';

    const nextBtn = document.createElement('button');
    nextBtn.textContent = 'Next →';
    nextBtn.type = 'button';
    nextBtn.style.cssText =
      'padding: 6px 12px; border-radius: 4px; border: none; cursor: pointer;';

    const titleSpan = document.createElement('span');
    titleSpan.style.cssText =
      'flex: 1 1 100%; text-align: center; font-weight: 600;';

    const counterSpan = document.createElement('span');
    counterSpan.style.cssText = 'font-size: 14px; opacity: 0.8;';

    const loadVideo = (index) => {
      videoFrame.innerHTML = '';

      const video = videoDataArray[index];

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&enablejsapi=1`;
      iframe.style.position = 'absolute';
      iframe.style.top = '0';
      iframe.style.left = '0';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
      );
      iframe.allowFullscreen = true;
      iframe.title = video.title || '';

      videoFrame.appendChild(iframe);

      titleSpan.textContent = video.title || '';
      counterSpan.textContent = `${index + 1} / ${videoDataArray.length}`;

      prevBtn.disabled = index === 0;
      nextBtn.disabled = index === videoDataArray.length - 1;
    };

    prevBtn.addEventListener('click', () => {
      if (currentIndex > 0) {
        currentIndex -= 1;
        loadVideo(currentIndex);
      }
    });

    nextBtn.addEventListener('click', () => {
      if (currentIndex < videoDataArray.length - 1) {
        currentIndex += 1;
        loadVideo(currentIndex);
      }
    });

    controls.appendChild(prevBtn);
    controls.appendChild(nextBtn);
    controls.appendChild(titleSpan);
    controls.appendChild(counterSpan);

    playlistContainer.appendChild(videoFrame);
    playlistContainer.appendChild(controls);

    container.appendChild(playlistContainer);

    // Start with first video
    loadVideo(0);
  }
}

customElements.define('video-modal-opener', VideoModalOpener);
